import { EProblemReportSortBy, EProblemReportSortOrder, EProblemReportStatus } from '@constants';
import { IProblemReportFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 20;

const ProblemReportService = sdk.getService('ProblemReportService');

interface IStore {
  page: number;
  limit: number;
  search?: string;
  filter?: IProblemReportFilters;
  sortBy?: EProblemReportSortBy;
  sortOrder?: EProblemReportSortOrder;
  setFilter: (filter?: IProblemReportFilters) => void;
  updateFilter: <K extends keyof IProblemReportFilters>(
    key: K,
    value?: IProblemReportFilters[K]
  ) => void;
  clearFilter: (key: keyof IProblemReportFilters) => void;
  setSearch: (search?: string) => void;
  setPageAndLimit: (page: number, limit: number) => void;
  resetFilters: () => void;
  setSort: (sortBy?: EProblemReportSortBy, sortOrder?: EProblemReportSortOrder) => void;
  clearSort: () => void;
}

const store = create<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  setFilter: (filter?: IProblemReportFilters) =>
    set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  updateFilter: (key, value) =>
    set(state => {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        const newFilter = { ...state.filter };
        delete newFilter[key];
        return { ...state, page: INITIAL_PAGE, filter: newFilter };
      }
      return { ...state, page: INITIAL_PAGE, filter: { ...state.filter, [key]: value } };
    }),
  clearFilter: key =>
    set(state => {
      const newFilter = { ...state.filter };
      delete newFilter[key];
      return { ...state, page: INITIAL_PAGE, filter: newFilter };
    }),
  setSearch: (search?: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  resetFilters: () =>
    set(state => ({
      ...state,
      page: INITIAL_PAGE,
      filter: undefined,
      sortBy: undefined,
      sortOrder: undefined
    })),
  setSort: (sortBy, sortOrder) =>
    set(state => ({ ...state, page: INITIAL_PAGE, sortBy, sortOrder })),
  clearSort: () =>
    set(state => ({ ...state, page: INITIAL_PAGE, sortBy: undefined, sortOrder: undefined }))
}));

export const useProblemReportsList = (tabStatus?: EProblemReportStatus[]) => {
  const {
    page,
    limit,
    filter,
    search,
    sortBy,
    sortOrder,
    setPageAndLimit,
    setFilter,
    updateFilter,
    clearFilter,
    setSearch,
    resetFilters,
    setSort,
    clearSort
  } = store();

  const queryKey = ['problem-reports', page, limit, filter, search, tabStatus, sortBy, sortOrder];

  const { isLoading, data, error } = useQuery({
    queryKey,
    queryFn: () =>
      ProblemReportService.getProblemReports({ page, limit }, search, {
        ...filter,
        ...(tabStatus?.length && { tabStatus }),
        ...(sortBy && { sortBy }),
        ...(sortOrder && { sortOrder })
      })
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    problemReports: data?.docs,
    setPageAndLimit,
    pagination,
    isLoading,
    error,
    filter,
    setFilter,
    updateFilter,
    clearFilter,
    search,
    setSearch,
    resetFilters,
    sortBy,
    sortOrder,
    setSort,
    clearSort,
    page,
    limit
  };
};
